import { format, formatDistanceToNow } from 'date-fns';
import { useCallback, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import {
  getPluginCloudfrontInvalidationUrl,
  getPluginTrackingDirectoryUrl,
  PLUGIN_RESOURCE_PREFIX,
  PLUGIN_TRACKING_ENTRIES,
  type PluginTrackingEntry,
} from '../pluginTrackingData';
import { getUploadCdnBase } from '../adminAssetUploadStore';

type PluginStatus = 'idle' | 'loading' | 'missing' | 'ready' | 'error';

type PluginTrackingState = {
  status: PluginStatus;
  lastModified: Date | null;
  contentLength: number | null;
  errorMessage: string | null;
};

type PluginTrackingRow = PluginTrackingEntry & PluginTrackingState;

const idleRowState: PluginTrackingState = {
  status: 'idle',
  lastModified: null,
  contentLength: null,
  errorMessage: null,
};

const loadingRowState: PluginTrackingState = {
  status: 'loading',
  lastModified: null,
  contentLength: null,
  errorMessage: null,
};

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function fetchPluginMetadata(
  entry: PluginTrackingEntry,
): Promise<PluginTrackingState> {
  const url = `${getUploadCdnBase()}${PLUGIN_RESOURCE_PREFIX}${entry.fileName}`;

  try {
    const response = await fetch(url, { method: 'HEAD' });

    if (response.status === 404) {
      return {
        status: 'missing',
        lastModified: null,
        contentLength: null,
        errorMessage: null,
      };
    }

    if (!response.ok) {
      return {
        status: 'error',
        lastModified: null,
        contentLength: null,
        errorMessage: `CDN returned ${response.status} ${response.statusText}`,
      };
    }

    const lastModifiedHeader = response.headers.get('last-modified');
    const contentLengthHeader = response.headers.get('content-length');

    if (!lastModifiedHeader) {
      return {
        status: 'error',
        lastModified: null,
        contentLength: null,
        errorMessage: 'CDN response missing Last-Modified header',
      };
    }

    const lastModified = new Date(lastModifiedHeader);
    if (Number.isNaN(lastModified.getTime())) {
      return {
        status: 'error',
        lastModified: null,
        contentLength: null,
        errorMessage: 'Invalid Last-Modified date from CDN',
      };
    }

    return {
      status: 'ready',
      lastModified,
      contentLength: contentLengthHeader ? Number(contentLengthHeader) : null,
      errorMessage: null,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      status: 'error',
      lastModified: null,
      contentLength: null,
      errorMessage: message,
    };
  }
}

export default function PluginTrackingSection() {
  const [rows, setRows] = useState<PluginTrackingRow[]>(() =>
    PLUGIN_TRACKING_ENTRIES.map((entry) => ({
      ...entry,
      ...idleRowState,
    })),
  );
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pluginTrackingDirectoryUrl = getPluginTrackingDirectoryUrl();
  const pluginCloudfrontInvalidationUrl = getPluginCloudfrontInvalidationUrl();

  const loadPluginMetadata = useCallback(async () => {
    setIsRefreshing(true);
    setRows(
      PLUGIN_TRACKING_ENTRIES.map((entry) => ({
        ...entry,
        ...loadingRowState,
      })),
    );

    const results = await Promise.all(
      PLUGIN_TRACKING_ENTRIES.map(async (entry) => {
        const metadata = await fetchPluginMetadata(entry);
        return { ...entry, ...metadata };
      }),
    );

    setRows(results);
    setHasLoaded(true);
    setIsRefreshing(false);
  }, []);

  return (
    <div className="bg-secondary mt-8 rounded-2xl p-4">
      <div className="bg-primary border-primary flex flex-col gap-4 rounded-lg border p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Text
              variant="studio/body/medium/24"
              className="text-primary"
              as="h2"
            >
              Plugin tracking
            </Text>
            <Text variant="studio/body/medium/sm" className="text-tertiary">
              Unreal Engine plugin builds at{' '}
              <span className="text-primary font-mono">
                {pluginTrackingDirectoryUrl}
              </span>
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={isRefreshing}
              onClick={() => void loadPluginMetadata()}
            >
              {isRefreshing ? 'Loading…' : hasLoaded ? 'Refresh' : 'Fetch status'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                window.open(
                  pluginCloudfrontInvalidationUrl,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Invalidate cache
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className="border-primary border-b">
                <th className="text-tertiary px-2 py-2 text-xs font-medium uppercase">
                  Build
                </th>
                <th className="text-tertiary px-2 py-2 text-xs font-medium uppercase">
                  File
                </th>
                <th className="text-tertiary px-2 py-2 text-xs font-medium uppercase">
                  Last updated
                </th>
                <th className="text-tertiary px-2 py-2 text-xs font-medium uppercase">
                  Size
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const downloadUrl = `${pluginTrackingDirectoryUrl}/${row.fileName}`;

                return (
                  <tr
                    key={row.fileName}
                    className="border-primary border-b last:border-b-0"
                  >
                    <td className="text-primary px-2 py-3 text-sm">
                      UE {row.unrealVersion} · {row.platform}
                    </td>
                    <td className="px-2 py-3">
                      <a
                        href={downloadUrl}
                        className="text-blue font-mono text-sm underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.fileName}
                      </a>
                    </td>
                    <td className="px-2 py-3 text-sm">
                      {row.status === 'idle' && (
                        <Text
                          variant="studio/body/medium/sm"
                          className="text-tertiary"
                        >
                          —
                        </Text>
                      )}
                      {row.status === 'loading' && (
                        <Text
                          variant="studio/body/medium/sm"
                          className="text-tertiary"
                        >
                          Loading…
                        </Text>
                      )}
                      {row.status === 'missing' && (
                        <Text
                          variant="studio/body/medium/sm"
                          className="text-tertiary"
                        >
                          Not uploaded
                        </Text>
                      )}
                      {row.status === 'error' && (
                        <Text
                          variant="studio/body/medium/sm"
                          className="text-red"
                        >
                          {row.errorMessage ?? 'Error'}
                        </Text>
                      )}
                      {row.status === 'ready' && row.lastModified && (
                        <div className="flex flex-col gap-0.5">
                          <Text
                            variant="studio/body/medium/sm"
                            className="text-primary"
                          >
                            {formatDistanceToNow(row.lastModified, {
                              addSuffix: true,
                            })}
                          </Text>
                          <Text
                            variant="studio/body/medium/xs"
                            className="text-tertiary"
                          >
                            {format(row.lastModified, 'MMM d, yyyy h:mm a')}
                          </Text>
                        </div>
                      )}
                    </td>
                    <td className="text-primary px-2 py-3 text-sm">
                      {formatFileSize(row.contentLength)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
